import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { GamesSchema, TAppState, TRPSGameConfig } from '../types/gamesSchema';
import {
  fetchCompleteNoMove,
  fetchContinueAwaiting,
  fetchGameFriends,
  fetchGameRating,
  fetchGameState,
  fetchJoinNextRoundGame,
  fetchLeaveGame,
  fetchMoveGame,
  fetchPlayGame,
  fetchState,
} from '../services/fetchGameService';
import NetworkError from 'shared/exceptions/network';
import { unSubscribeGameChannel } from 'shared/helpers/socketHook/unsubscribeGameChannel';

const initialState: GamesSchema = {
  random: {
    isRandom: false,
    lockRandom: false,
    stopMove: false,
  },
  appState: {
    loading: false,
  },
  activeGame: {
    loading: false,
    currentGame: {},
    moveLoading: false,
  },
  rating: {
    loading: false,
  },
  friends: {
    loading: false,
  },
};

export const gamesSlice = createSlice({
  name: 'games',
  initialState,
  reducers: {
    setCurrentGameState(
      state,
      action: PayloadAction<TRPSGameConfig | undefined>,
    ) {
      state.activeGame.currentGame.rock_scissors_paper = action.payload;
    },
    setGlobalState(state, action: PayloadAction<TAppState>) {
      state.appState.data = action.payload;
      // if (!action.payload?.current_games?.rock_scissors_paper?.id) {
      //   console.log('CLEAR ACTIVE STATE WITH GLOBAL');
      //   state.activeGame.currentGame.rock_scissors_paper = undefined;
      // }
    },
    cancelActiveGameError(state) {
      state.activeGame.error = undefined;
    },
    onRandomStart(state) {
      state.random.isRandom = true;
    },
    onRandomCancel(state) {
      state.random.isRandom = false;
    },
    setLockRandom(state, action: PayloadAction<boolean>) {
      state.random.lockRandom = action.payload;
    },
    setStopMove(state, action: PayloadAction<boolean>) {
      state.random.stopMove = action.payload;
    },
  },
  extraReducers: builder => {
    // games/fetchState
    builder
      .addCase(fetchState.pending, state => {
        state.appState.loading = true;
        state.appState.error = undefined;
      })
      .addCase(fetchState.fulfilled, (state, action) => {
        state.appState.loading = false;
        state.appState.error = undefined;
        state.appState.data = action.payload.data.data;
      })
      .addCase(fetchState.rejected, (state, action) => {
        state.appState.loading = false;
        const message = (action.payload as NetworkError).message;

        state.appState.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchGameState
    builder
      .addCase(fetchGameState.pending, state => {
        // state.activeGame.loading = true;
        state.activeGame.error = undefined;
      })
      .addCase(fetchGameState.fulfilled, (state, action) => {
        state.activeGame.loading = false;
        state.activeGame.error = undefined;
        state.activeGame.currentGame.rock_scissors_paper =
          action.payload.data.data;
      })
      .addCase(fetchGameState.rejected, (state, action) => {
        state.activeGame.loading = false;
        const message = (action.payload as NetworkError).message;

        state.activeGame.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchCompleteNoMove
    builder
      .addCase(fetchCompleteNoMove.pending, state => {
        state.activeGame.loading = true;
        state.activeGame.error = undefined;
      })
      .addCase(fetchCompleteNoMove.fulfilled, (state, action) => {
        state.activeGame.loading = false;
        state.activeGame.error = undefined;
        console.log(action.payload.data.data);
        if (action?.payload?.data?.data?.id) {
          state.activeGame.currentGame.rock_scissors_paper =
            action.payload.data.data;
        }
      })
      .addCase(fetchCompleteNoMove.rejected, (state, action) => {
        state.activeGame.loading = false;
        const message = (action.payload as NetworkError).message;

        state.activeGame.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchGameRating
    builder
      .addCase(fetchGameRating.pending, state => {
        state.rating.loading = true;
        state.rating.error = undefined;
      })
      .addCase(fetchGameRating.fulfilled, (state, action) => {
        state.rating.loading = false;
        state.rating.error = undefined;
        state.rating.rock_scissors_paper = action.payload.data.data;
      })
      .addCase(fetchGameRating.rejected, (state, action) => {
        state.rating.loading = false;
        const message = (action.payload as NetworkError).message;

        state.rating.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchGameFriends
    builder
      .addCase(fetchGameFriends.pending, state => {
        state.friends.loading = true;
        state.friends.error = undefined;
      })
      .addCase(fetchGameFriends.fulfilled, (state, action) => {
        state.friends.loading = false;
        state.friends.error = undefined;
        state.friends.rock_scissors_paper = action.payload.data.data;
      })
      .addCase(fetchGameFriends.rejected, (state, action) => {
        state.friends.loading = false;
        const message = (action.payload as NetworkError).message;

        state.friends.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchPlayGame
    builder
      .addCase(fetchPlayGame.pending, state => {
        state.activeGame.loading = true;
        state.activeGame.error = undefined;
      })
      .addCase(fetchPlayGame.fulfilled, (state, action) => {
        state.activeGame.loading = false;
        state.activeGame.error = undefined;
        state.activeGame.currentGame.rock_scissors_paper =
          action.payload.data.data;
      })
      .addCase(fetchPlayGame.rejected, (state, action) => {
        state.activeGame.loading = false;
        const message = (action.payload as NetworkError).message;

        state.activeGame.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchLeaveGame
    builder.addCase(fetchLeaveGame.fulfilled, (state, action) => {
      unSubscribeGameChannel(action.meta.arg.tgId, action.meta.arg.gameId);
      state.activeGame.currentGame.rock_scissors_paper = undefined;
    });

    // games/fetchMoveGame
    builder
      .addCase(fetchMoveGame.pending, state => {
        state.activeGame.loading = true;
        state.activeGame.moveLoading = true;
        state.activeGame.error = undefined;
      })
      .addCase(fetchMoveGame.fulfilled, (state, action) => {
        state.activeGame.currentGame.rock_scissors_paper =
          action.payload.data.data;
        state.activeGame.loading = false;
        state.activeGame.error = undefined;
        state.activeGame.moveLoading = false;
      })
      .addCase(fetchMoveGame.rejected, (state, action) => {
        state.activeGame.loading = false;
        state.activeGame.moveLoading = false;
        const message = (action.payload as NetworkError).message;
        state.activeGame.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchJoinNextRoundGame
    builder
      .addCase(fetchJoinNextRoundGame.pending, state => {
        state.activeGame.loading = true;
        state.activeGame.error = undefined;
      })
      .addCase(fetchJoinNextRoundGame.fulfilled, (state, action) => {
        state.activeGame.loading = false;
        state.activeGame.error = undefined;
        state.activeGame.currentGame.rock_scissors_paper =
          action.payload.data.data;
      })
      .addCase(fetchJoinNextRoundGame.rejected, (state, action) => {
        state.activeGame.loading = false;
        const message = (action.payload as NetworkError).message;
        state.activeGame.error = {
          ...(action.payload as NetworkError),
          message,
        };
      });

    // games/fetchContinueAwaiting
    builder.addCase(fetchContinueAwaiting.pending, state => {
      if (state.activeGame.currentGame.rock_scissors_paper) {
        state.activeGame.currentGame.rock_scissors_paper.awaiting_players_timer =
          undefined;
      }
    });
  },
});

export const { actions: gamesActions } = gamesSlice;
export const { reducer: gamesReducer } = gamesSlice;
